import type { FullPermission } from '../types/Role';
import type SinatraState from '../types/SinatraState';

type UserState = SinatraState['user'];

/**
 * Return the organizations of a logged-in user, or undefined when there is no
 * profile to read from — so "we don't know this user's organizations" stays
 * distinct from "this user belongs to none".
 *
 * The type says a profile always carries an organizations array. Scorpius does
 * not honor that: it can answer /user with a 200 whose body omits the list, and
 * the reducer stores that body and marks the user logged in regardless. So
 * isLoggedIn alone does not guarantee organizations exists, and the runtime
 * guard stays even though the type calls it unreachable.
 */
function selectUserOrganizations(user: UserState) {
  if (!user?.isLoggedIn) {
    return undefined;
  }
  const { organizations } = user.profile ?? {};
  return Array.isArray(organizations) ? organizations : undefined;
}

/**
 * Find the permissions of the role matching appName for the current user.
 * @returns the role's permissions; [] when no role matches; undefined when the
 * user's profile is unavailable
 */
export function selectUserPermissionsByApp(
  user: UserState,
  appName: string,
): string[] | undefined {
  const organizations = selectUserOrganizations(user);
  if (!organizations) {
    return undefined;
  }
  let permissions: string[] = [];
  for (const org of organizations) {
    for (const role of org?.roles ?? []) {
      if (role?.application === appName) {
        permissions = role.permissions ?? [];
      }
    }
  }
  return permissions;
}

/**
 * Find the full permissions of the role matching appName for the current user.
 * @returns the role's permissions_full; [] when no role matches; undefined when
 * the user's profile is unavailable
 */
export function selectFullUserPermissionsByApp(
  user: UserState,
  appName: string,
): FullPermission[] | undefined {
  const organizations = selectUserOrganizations(user);
  if (!organizations) {
    return undefined;
  }
  let permissionsFull: FullPermission[] = [];
  for (const org of organizations) {
    for (const role of org?.roles ?? []) {
      if (role?.application === appName) {
        permissionsFull = role.permissions_full ?? [];
      }
    }
  }
  return permissionsFull;
}

const MEDICALERT = 'medicalert';
const PROFILE_INFO = 'profile_info';

/**
 * Normalize user permissions due to legacy naming conventions
 * @param permissions - User permissions
 */
function normalizeUserPermissions(permissions: string[]): string[] {
  return permissions.map(p => {
    if (p.toLowerCase() === PROFILE_INFO) {
      return MEDICALERT;
    }
    // TODO: return p.lowerCase() in future release to normalize inconsistency with capitaliziation
    return p;
  });
}

export default normalizeUserPermissions;
